/* eslint-disable no-restricted-imports -- this wrapper is the single canonical entry point for dayjs; all consumers must import from `@common/services/datetime`. */
import dayjs, { type Dayjs as DatetimeType, type PluginFunc } from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import duration from 'dayjs/plugin/duration';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import localeData from 'dayjs/plugin/localeData';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import updateLocale from 'dayjs/plugin/updateLocale';
import utc from 'dayjs/plugin/utc';
import weekday from 'dayjs/plugin/weekday';

dayjs.extend(advancedFormat);
dayjs.extend(customParseFormat);
dayjs.extend(duration);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(localeData);
dayjs.extend(relativeTime);
dayjs.extend(updateLocale);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(weekday);

// === Moment-parity humanize for durations (WORK-6090, M5.2 — WORKS-330) ===
//
// dayjs's duration `.humanize()` delegates to `now.add(this).fromNow()`, which is
// calendar-dependent and divides by a flat 30-day month. moment computes humanize
// straight from the duration with a fixed month length (daysToMonths:
// days * 4800 / 146097 ≈ 30.4369 days/month), so the two diverge by one bucket
// near a rounding boundary (e.g. ~106 days → moment "3 months", dayjs "4 months")
// and dayjs's answer drifts with today's calendar. We replace duration.humanize
// with moment's exact `relativeTime` bucketing so cutover code keeps moment's
// labels and the result is deterministic. Scoped to duration.humanize only —
// `fromNow()` / `relativeTime` on Dayjs instances are untouched. Verified
// identical to moment 2.29.4 across 500k+ durations (both signs, seconds→years).

// moment's default relativeTime thresholds (en).
const MOMENT_DURATION_THRESHOLDS = {
  ss: 44,
  s: 45,
  m: 45,
  h: 22,
  d: 26,
  M: 11,
};
// moment's daysToMonths factor: days * 4800 / 146097.
const DURATION_DAYS_PER_MONTH = 146097 / 4800;
// moment's en relativeTime strings — identical to dayjs's en set plus the `ss`
// ("%d seconds") key dayjs omits. Inlined (the app is en-only) so humanized
// output matches moment exactly without depending on dayjs locale internals.
const DURATION_RELATIVE_TIME = {
  future: 'in %s',
  past: '%s ago',
  s: 'a few seconds',
  ss: '%d seconds',
  m: 'a minute',
  mm: '%d minutes',
  h: 'an hour',
  hh: '%d hours',
  d: 'a day',
  dd: '%d days',
  M: 'a month',
  MM: '%d months',
  y: 'a year',
  yy: '%d years',
} as const;

function momentParityHumanize(
  this: { asMilliseconds: () => number },
  withSuffix?: boolean
): string {
  const ms = this.asMilliseconds();
  const abs = Math.abs(ms);
  const days = abs / 86_400_000;
  const { round } = Math;
  const seconds = round(abs / 1_000);
  const minutes = round(abs / 60_000);
  const hours = round(abs / 3_600_000);
  const roundedDays = round(days);
  const months = round(days / DURATION_DAYS_PER_MONTH);
  const years = round(days / DURATION_DAYS_PER_MONTH / 12);
  const th = MOMENT_DURATION_THRESHOLDS;

  const bucket: [keyof typeof DURATION_RELATIVE_TIME, number?] =
    seconds <= th.ss
      ? ['s']
      : seconds < th.s
        ? ['ss', seconds]
        : minutes <= 1
          ? ['m']
          : minutes < th.m
            ? ['mm', minutes]
            : hours <= 1
              ? ['h']
              : hours < th.h
                ? ['hh', hours]
                : roundedDays <= 1
                  ? ['d']
                  : roundedDays < th.d
                    ? ['dd', roundedDays]
                    : months <= 1
                      ? ['M']
                      : months < th.M
                        ? ['MM', months]
                        : years <= 1
                          ? ['y']
                          : ['yy', years];

  const [key, value] = bucket;
  const phrase = DURATION_RELATIVE_TIME[key].replace('%d', String(value));
  if (!withSuffix) {
    return phrase;
  }
  // moment treats a zero/negative duration as past (`diff > 0 ? future : past`).
  const direction =
    ms > 0 ? DURATION_RELATIVE_TIME.future : DURATION_RELATIVE_TIME.past;
  return direction.replace('%s', phrase);
}

// The duration plugin is extended above; all Duration instances share one
// prototype, so patching it in place covers every `datetime.duration(...)`
// (including the shadow runner's dayjs side, which builds durations here).
Object.getPrototypeOf(dayjs.duration(0)).humanize = momentParityHumanize;

// Matches a trailing UTC offset in an ISO 8601 string: `+05:00`, `-0530`, `+08`.
// Groups: 1 = sign, 2 = hours, 3 = minutes (optional).
const ISO_OFFSET_SUFFIX = /([+-])(\d{2}):?(\d{2})?$/;

// === Moment-parity hardening for invalid dates (WORK-6090, M5.2 hotspot fix) ===
//
// The moment→dayjs shadow window flagged two real divergences in how the two
// libraries treat invalid input. moment is strict; dayjs is lenient. Cutover
// code that swaps `moment(x)` → `datetime(x)` must keep moment's contract or it
// silently changes behavior. The two fixes below restore parity. The one
// divergence we deliberately do NOT fix is the cosmetic `Invalid date` (moment)
// vs `Invalid Date` (dayjs) casing in `.format()` output — that is documented as
// an accepted benign quirk in the cutover PR.

// Matches a leading ISO calendar date (`YYYY-MM-DD`) followed by a date/time
// separator or end-of-string. Group 1 is the date portion only.
const ISO_DATE_PREFIX = /^(\d{4}-\d{2}-\d{2})(?:[T ]|$)/;

// moment rejects calendar-overflow dates (`2021-02-30`, `2021-13-45`,
// `0000-00-00`) as Invalid; dayjs's lenient parser silently rolls them into a
// real date (`2021-03-02`, `2022-02-14`, `1899-11-30`), so `.isValid()` returns
// true and `.valueOf()` returns a plausible-but-wrong timestamp. We restore
// moment's strictness by validating just the calendar-date portion with
// customParseFormat strict mode. Only the date part is checked: it is
// timezone-independent, so a genuinely valid instant carrying an offset/Z time
// component (e.g. `2024-06-08T23:30:00-08:00`, whose UTC date differs from its
// local date) is never false-flagged.
function hasOverflowDatePortion(input: string): boolean {
  const match = input.match(ISO_DATE_PREFIX);
  if (!match) {
    return false;
  }
  return !dayjs(match[1], 'YYYY-MM-DD', true).isValid();
}

// moment(<invalid>).toISOString() returns `null`; dayjs delegates to the native
// Date#toISOString(), which throws `RangeError: Invalid time value` on an
// invalid instant. Consumers migrated off moment expect the null, not an
// exception, so we restore moment's contract at the prototype level (covers
// every Dayjs instance, including those built via `.utc()` / `.tz()`). moment's
// own typings declare `toISOString(): string` while returning null at runtime
// for an invalid date; we mirror that exact typed-string / runtime-null shape.
const invalidToISOStringParity: PluginFunc = (_option, DayjsClass) => {
  const originalToISOString = DayjsClass.prototype.toISOString;
  DayjsClass.prototype.toISOString = function toISOString(
    this: DatetimeType
  ): string {
    return this.isValid()
      ? originalToISOString.call(this)
      : (null as unknown as string);
  };
};
dayjs.extend(invalidToISOStringParity);

// === Accepted divergence: `z` timezone-abbreviation token (WORK-6090, M5.2 — WORKS-33B) ===
//
// The shadow window flagged that dayjs's lowercase `z` token renders a `GMT±H`
// string (`GMT-3`) where moment-timezone renders the IANA abbreviation (`-03`,
// `IST`, …), and renders `zz` twice. We deliberately do NOT reproduce moment's
// form: matching it for every zone would mean shipping moment-timezone's
// abbreviation database inside the dayjs wrapper, and `GMT-3` is an accurate,
// unambiguous label that simply becomes the norm once moment is gone post-cutover.
// Recorded as an accepted quirk (option 3 — document-and-accept) in the cutover PR
// and Asana WORKS-33B, not fixed here.

// A real ISO 8601 zone designator (`+05:00`, `-0530`, `+08`) lives in the TIME
// portion, after the `T` (or space) that separates the date from the time.
// Scanning the whole string with ISO_OFFSET_SUFFIX false-matches a bare calendar
// date's own trailing `-DD` — `2026-06-28` reads as a bogus `-28:00` offset — which
// skips the zoneless→UTC branch in parseZone and renders a wrong instant: the
// scheduling/shifts shadow hotspot regression (WORK-6090/WORK-6365 Defect A;
// `moment.parseZone('2026-06-28')` is UTC). Restrict the offset match to the
// substring after the date so a date-only input is correctly treated as zoneless.
function isoOffsetMatch(input: string): RegExpMatchArray | null {
  const timeSeparator = input.search(/[T ]/);
  if (timeSeparator === -1) {
    return null;
  }
  return input.slice(timeSeparator).match(ISO_OFFSET_SUFFIX);
}

// True when an ISO string carries an explicit zone — a trailing `Z` or a real
// offset in its time portion. Mirrors moment's input-dependent zone handling: a
// zoneless string is read as UTC by parseZone and as wall-time-in-zone by tz,
// while a zone-bearing string keeps its own instant (WORK-6365).
function hasExplicitZone(input: string): boolean {
  return input.endsWith('Z') || isoOffsetMatch(input) !== null;
}

export function parseZone(input: string | DatetimeType): DatetimeType {
  // A real datetime passes through unchanged. Any OTHER non-string
  // (`null`, `NaN`, junk) becomes an Invalid date, matching
  // `moment.parseZone(null)` — which formats to "Invalid date". Without this
  // guard dayjs let `null` fall through and the chained `.tz()` threw a
  // TypeError, crashing the scheduling/shifts "Last Updated" column on cutover
  // (WORK-6090, WORKS-36S; Sentry 515b126d). Deliberate divergence: Moment is
  // NOT uniformly lenient — `moment.parseZone(undefined) === now` and
  // `moment.parseZone(<number>) === epoch` — whereas this broad guard returns
  // Invalid for those too. Both are `string | DatetimeType` type-contract
  // violations, do not occur for the scheduling GraphQL fields, and would
  // already throw under dayjs, so returning Invalid is strictly safer than the
  // old passthrough.
  if (isDatetime(input)) {
    return input;
  }
  if (typeof input !== 'string') {
    return dayjs(NaN);
  }

  // Reject calendar-overflow dates up front so parseZone matches moment.parseZone,
  // which returns an Invalid date for these rather than rolling them over.
  if (hasOverflowDatePortion(input)) {
    return dayjs(NaN);
  }

  if (input.endsWith('Z')) {
    return dayjs.utc(input);
  }

  const match = isoOffsetMatch(input);
  // A zoneless datetime (no trailing `Z`, no `±HH:MM` suffix) must be read as
  // UTC to match `moment.parseZone`, which assigns offset 0 to an offset-less
  // input. `dayjs(input)` would parse it in the browser's LOCAL zone instead —
  // same wall clock, different instant — which surfaced as the scheduling/shifts
  // shadow hotspot (WORK-6090: WORKS-33D/E/F, `moment.parseZone(shift.startDatetime)`
  // when the datetime arrived without an offset). Use `dayjs.utc` so the zone, and
  // therefore every chained `.add`/`.date`/`.format`, matches moment.
  if (!match) {
    return dayjs.utc(input);
  }

  const sign = match[1] === '+' ? 1 : -1;
  const hours = parseInt(match[2], 10);
  const minutes = parseInt(match[3] || '0', 10);
  const totalMinutes = sign * (hours * 60 + minutes);

  return dayjs(input).utcOffset(totalMinutes);
}

export function isDatetime(value: unknown): value is DatetimeType {
  return dayjs.isDayjs(value);
}

// moment tags its instances with `_isAMomentObject` — the same marker
// `moment.isMoment` checks. We duck-type on it so this file never imports
// moment (keeping the moment island confined to moment-bridge.ts).
function isMomentObject(
  value: unknown
): value is { format: (template?: string) => string } {
  return (
    typeof value === 'object' &&
    value !== null &&
    (value as { _isAMomentObject?: boolean })._isAMomentObject === true
  );
}

// A *local-mode* moment (built by `moment(input)` with no zone conversion)
// recomputes its UTC offset whenever arithmetic moves it across a DST boundary —
// `moment.isLocal()` is true only for that mode and false for UTC-mode
// (`moment.utc(...)`) and fixed-offset (`moment.parseZone(...)` / `.utcOffset(n)`)
// moments, whose offset is intentionally frozen. Duck-typed on the public
// `isLocal()` method like isMomentObject so this file never imports moment; a
// stub without `isLocal` (the parity specs' `momentLike`) is treated as
// non-local and keeps the offset-preserving round-trip path.
function isLocalModeMoment(value: unknown): boolean {
  if (!isMomentObject(value)) {
    return false;
  }
  const probe = value as unknown as { isLocal?: () => boolean };
  return typeof probe.isLocal === 'function' && probe.isLocal();
}

// Reads the absolute instant off a (duck-typed) moment object for the local-mode
// construction path — paired with isLocalModeMoment, never called on its own.
function momentInstant(value: {
  format: (template?: string) => string;
}): number {
  return (value as unknown as { valueOf: () => number }).valueOf();
}

// moment's default `.format()` emits ISO 8601 *without* milliseconds, so
// round-tripping a Moment object through it silently truncates sub-second
// precision: scheduling/shifts WORKS-333 — `moment(currentDate).valueOf()` kept
// `…038` while the dayjs equivalent landed on the whole second (`…000`). Round-
// trip through an explicit millisecond-precision ISO format instead; its `Z`
// token still carries the offset, so both the zone (the #7198 fix) and the
// milliseconds survive.
const MOMENT_ROUNDTRIP_FORMAT = 'YYYY-MM-DDTHH:mm:ss.SSSZ';

// moment's own isDate check (realm-safe, matches moment.isDate). A Date passed
// with a format argument needs the same format-ignoring treatment as a moment
// object — moment uses the instant and ignores the format for both.
function isDateObject(value: unknown): value is Date {
  return Object.prototype.toString.call(value) === '[object Date]';
}

// True when no construction args beyond the first were supplied. The shadow
// factory always calls `datetime(input, format, strict)` with the trailing two
// `undefined`, so a plain `rest.length === 0` check would miss the bare
// single-arg call shape coming through that path.
function hasNoExtraArgs(rest: unknown[]): boolean {
  return rest.every((arg) => arg === undefined);
}

// Bare construction: `datetime('2021-02-30')` returns an Invalid date like
// moment, instead of dayjs's silent rollover. Only a single bare string arg is
// intercepted; explicit-format calls (`datetime(str, fmt)`) keep dayjs's
// caller-controlled parsing.
function createDatetime(
  date?: dayjs.ConfigType,
  ...rest: unknown[]
): DatetimeType {
  // A *moment* object (not a dayjs one) handed to datetime() — e.g. an
  // incremental cutover where an upstream module still produces moments.
  // moment(m) clones m preserving its zone mode, so a UTC-mode moment renders
  // in UTC (`2026-05-31T15:07:40Z`); dayjs(m), seeing a foreign object, only
  // reads m.valueOf() and falls back to LOCAL, silently shifting the displayed
  // offset (`2026-05-31T11:07:40-04:00`) — the scheduling/shifts shadow hotspot
  // (WORK-6090). Round-trip through the moment's own offset- and millisecond-
  // preserving ISO string so the zone and sub-second precision survive, exactly
  // as momentToDayjs() does. dayjs objects fall through: dayjs(dayjsObj) already
  // clones and preserves the zone.
  //
  // The format/strict args are intentionally ignored here: moment consults a
  // format ONLY for string input, so `moment(m, 'HH:mm')` clones m and ignores
  // the format just like the bare `moment(m)`. (Originally gated on
  // `hasNoExtraArgs`; ungated to also cover the `moment(m, fmt)` call shape —
  // the residual scheduling/shifts divergence #7198 left, where dayjs's
  // customParseFormat ran on the object and returned an Invalid date.)
  if (isMomentObject(date)) {
    // A local-mode moment must become a zone-aware (local) dayjs, not a frozen-
    // offset one. parseZone(m.format()) bakes in the moment's *current* offset, so
    // later month/day arithmetic that crosses a DST boundary keeps the stale
    // offset where the local moment recomputes it — the scheduling/shifts
    // divergence (WORK-6090, WORKS-340/341): a June-EDT base subtract(1,"month")'d
    // (or startOf("month")'d) into EST rendered `-04:00` where moment recomputes
    // `-05:00`. dayjs(instant) renders in the same system zone as the local
    // moment, so the display matches at construction AND recomputes under
    // arithmetic; valueOf() keeps the milliseconds (WORKS-333). UTC- and
    // fixed-offset moments keep the offset-preserving round-trip below — their
    // offset is meant to stay frozen (WORKS-33D/E/F).
    if (isLocalModeMoment(date)) {
      return (dayjs as (...args: unknown[]) => DatetimeType)(
        momentInstant(date)
      );
    }
    return parseZone(date.format(MOMENT_ROUNDTRIP_FORMAT));
  }
  // A *Date* object handed in *with* a format/strict argument: moment ignores
  // the format for Date input and uses the instant, but dayjs's
  // customParseFormat runs on the Date and returns an Invalid date. Drop the
  // extra args and let dayjs parse the Date alone, matching moment (WORK-6090,
  // residual of #7198). Scoped to Date (plus the Moment object above): moment
  // does NOT blanket-ignore the format for other inputs — `moment(<number>,
  // 'HH:mm')` is itself Invalid and dayjs already agrees, so numbers / foreign
  // objects keep the caller's args and stay on the default path.
  if (!hasNoExtraArgs(rest) && isDateObject(date)) {
    return (dayjs as (...args: unknown[]) => DatetimeType)(date);
  }
  if (
    hasNoExtraArgs(rest) &&
    typeof date === 'string' &&
    hasOverflowDatePortion(date)
  ) {
    return dayjs(NaN);
  }
  return (dayjs as (...args: unknown[]) => DatetimeType)(date, ...rest);
}

// `datetime.utc('2021-02-30')` mirrors `moment.utc(...)` strictness too, since
// cutover maps `moment.utc(x)` → `datetime.utc(x)`.
const createUtcDatetime = ((
  date?: dayjs.ConfigType,
  ...rest: unknown[]
): DatetimeType => {
  // Mirror createDatetime's object-input parity on the UTC path: moment.utc
  // ignores a format argument for a Moment / Date input and keeps the instant
  // (in UTC mode), but dayjs.utc's customParseFormat returns an Invalid date. A
  // moment object round-trips through its zone-preserving string and is then
  // converted to UTC, so an offset moment's instant survives
  // (`moment.utc(parseZone('…-05:00'))` → the equivalent `…Z`); a Date uses its
  // instant directly. Numbers / foreign objects keep the caller's args — moment
  // does not blanket-ignore the format for them, and dayjs already agrees
  // (WORK-6090, residual of #7198).
  if (isMomentObject(date)) {
    // Same local-mode parity as createDatetime: a local moment yields a
    // zone-aware dayjs (from the instant) before `.utc()`, so the underlying
    // instant — and any later DST-crossing arithmetic — matches moment.utc(m)
    // (WORK-6090, WORKS-340/341). UTC/fixed-offset moments keep the round-trip.
    if (isLocalModeMoment(date)) {
      return dayjs(momentInstant(date)).utc();
    }
    return parseZone(date.format(MOMENT_ROUNDTRIP_FORMAT)).utc();
  }
  if (!hasNoExtraArgs(rest) && isDateObject(date)) {
    return dayjs.utc(date);
  }
  if (
    rest.length === 0 &&
    typeof date === 'string' &&
    hasOverflowDatePortion(date)
  ) {
    return dayjs.utc(NaN);
  }
  return (dayjs.utc as (...args: unknown[]) => DatetimeType)(date, ...rest);
}) as typeof dayjs.utc;

// moment-parity for `moment.tz(input, zone)` (moment-timezone). moment reads a
// ZONELESS string as wall-time IN `zone`, but honors an explicit offset/`Z` and
// uses the absolute instant of a Date/number/Moment. Each bare dayjs primitive
// matches only half of that: `dayjs.tz(input, zone)` reads wall-time as `zone`
// (right for zoneless) but ignores an explicit offset; `dayjs(input).tz(zone)`
// honors the instant (right for zone-bearing / non-string) but reads a zoneless
// string in the RUNNER's zone. The shadow's tz side used the latter, so
// `moment.tz(zonelessShiftDatetime, shiftZone)` diverged for every viewer whose
// runner zone differed from the shift's zone — the scheduling/shifts regression
// (WORK-6365 Defect B). Branch on the input to match moment across both. Cutover
// maps `moment.tz(x, z)` → `datetime.tz(x, z)`, so the fix lives in the wrapper.
const createTzDatetime = Object.assign(
  ((input?: dayjs.ConfigType, zone?: string): DatetimeType => {
    if (
      typeof input === 'string' &&
      zone !== undefined &&
      !hasExplicitZone(input)
    ) {
      return dayjs.tz(input, zone);
    }
    return createDatetime(input).tz(zone as string);
  }) as typeof dayjs.tz,
  // Preserve dayjs.tz's own statics (`guess`, `setDefault`) on the override.
  dayjs.tz
);

export const datetime = Object.assign(createDatetime, dayjs, {
  utc: createUtcDatetime,
  tz: createTzDatetime,
}) as typeof dayjs;

// Today's date as an ISO `YYYY-MM-DD` string in the active timezone. Prefer this over
// `new Date().toISOString().slice(0, 10)`, which is UTC-based and drifts across the day
// boundary in non-UTC zones.
export const todayIso = (): string => datetime().format('YYYY-MM-DD');

export type { DatetimeType };
